import { useMemo } from 'react'
import { createChartData, createChartOptions } from '../utils/chartUtils'

interface ChartDataProps {
  isSaved: boolean
  currentNumAmalgamFillings: string
  adjustedAmalgamFillings?: string
  amalgamTotalTeeth: string
  adjustedTotalAmalgamFillings?: string
  currentRootCanals: string
  adjustedRootCanals?: string
  rootCanalsTotal: string
  adjustedTotalRootCanals?: string
  currentImplants: string
  adjustedImplants?: string
  implantTotalTeeth: string
  adjustedTotalImplants?: string
}

/**
 * Hook to generate chart data and options for dental visualizations
 */
export default function useChartData({
  isSaved,
  currentNumAmalgamFillings,
  adjustedAmalgamFillings,
  amalgamTotalTeeth,
  adjustedTotalAmalgamFillings,
  currentRootCanals,
  adjustedRootCanals,
  rootCanalsTotal,
  adjustedTotalRootCanals,
  currentImplants,
  adjustedImplants,
  implantTotalTeeth,
  adjustedTotalImplants,
}: ChartDataProps) {
  // Generate Amalgam chart data
  const amalgamChartData = useMemo(() => {
    const current =
      Number(isSaved ? adjustedAmalgamFillings : currentNumAmalgamFillings) || 0
    const total =
      Number(isSaved ? adjustedTotalAmalgamFillings : amalgamTotalTeeth) || 0
    return createChartData(current, total, 'Amalgams', '#F56565') // Red
  }, [
    isSaved,
    adjustedAmalgamFillings,
    currentNumAmalgamFillings,
    adjustedTotalAmalgamFillings,
    amalgamTotalTeeth,
  ])

  // Generate Root Canal chart data
  const rootCanalChartData = useMemo(() => {
    const current =
      Number(isSaved ? adjustedRootCanals : currentRootCanals) || 0
    const total =
      Number(isSaved ? adjustedTotalRootCanals : rootCanalsTotal) || 0
    return createChartData(current, total, 'Root Canals', '#ED8936') // Orange
  }, [
    isSaved,
    adjustedRootCanals,
    currentRootCanals,
    adjustedTotalRootCanals,
    rootCanalsTotal,
  ])

  // Generate Implant chart data
  const implantChartData = useMemo(() => {
    const current = Number(isSaved ? adjustedImplants : currentImplants) || 0
    const total =
      Number(isSaved ? adjustedTotalImplants : implantTotalTeeth) || 0
    return createChartData(current, total, 'Implants', '#9F7AEA') // Purple
  }, [
    isSaved,
    adjustedImplants,
    currentImplants,
    adjustedTotalImplants,
    implantTotalTeeth,
  ])

  // Create chart options - these don't depend on state so they don't need to be memoized
  const amalgamChartOptions = createChartOptions('Amalgam Fillings')
  const rootCanalChartOptions = createChartOptions('Root Canals')
  const implantChartOptions = createChartOptions('Dental Implants')

  return {
    amalgamChartData,
    amalgamChartOptions,
    rootCanalChartData,
    rootCanalChartOptions,
    implantChartData,
    implantChartOptions,
  }
}
